import { createRef, Component, Fragment } from "react";
import { array, bool, func, object, string } from "prop-types";
import isEqual from "lodash/isEqual";

import { SITE_SHOW_STATUSES } from "../../../constants";
import { formatTitleCase, getShortDate } from "../../../helpers";

import SourceTypeLink from "../../source-type-link";
import ListingStacked from "./Results.layout-stacked";
import ResultsListingCard from "./Results.layout-card";

class ResultsListing extends Component {
  constructor(props) {
    super(props);

    this.formatTrackingEventLabel = this.formatTrackingEventLabel.bind(this);
    this.getChanges = this.getChanges.bind(this);
    this.handleCompare = this.handleCompare.bind(this);
    this.handleWatch = this.handleWatch.bind(this);
    this.setIsCompared = this.setIsCompared.bind(this);
    this.setIsWatched = this.setIsWatched.bind(this);

    this.watchButton = createRef();

    this.state = {
      changes: null,
      isCompared: false,
      isWatched: false,
      openWatch: false,
      showStatus:
        !!this.props.listing &&
        SITE_SHOW_STATUSES.includes(this.props.listing.status),
    };
  }

  /**
   * Format tracking event label
   * @param {object} listing
   * @public
   */
  formatTrackingEventLabel(listing) {
    let labelParts = [];

    if (!!listing.year) {
      labelParts.push(listing.year);
    }

    if (!!listing.make) {
      labelParts.push(listing.make);
    }

    if (!!listing.make && !!listing.model) {
      labelParts.push("–");
    }

    if (!!listing.model) {
      labelParts.push(listing.model);
    }

    if (!!listing.equipmentId) {
      labelParts.push(`(${listing.equipmentId})`);
    }

    return labelParts.length > 0 ? labelParts.join(" ") : "listing";
  }

  /**
   * Get changes
   * @public
   */
  getChanges() {
    const { listing, showChanges, showUpdatedDate } = this.props;

    if (
      !showChanges ||
      !listing ||
      (!!listing && !listing.hasOwnProperty("lastChange"))
    ) {
      this.setState({ changes: null });
      return;
    }

    const { lastChange } = listing;
    let changes;

    try {
      const parsed = JSON.parse(lastChange);
      changes = parsed || "";
    } catch (e) {
      changes = lastChange || "";
    }

    if (!!changes && typeof changes === "object" && !Array.isArray(changes)) {
      changes = Object.keys(changes).map(k => changes[k]);
    }

    let formatted;

    if (!!changes && Array.isArray(changes) && !!changes.length) {
      if (listing.status === "remove") {
        formatted = "This listing was removed from the site.";
      } else if (changes.indexOf("new") > -1) {
        formatted =
          "This listing was just added to HoltUsed.com — USED FEELS NEW AGAIN™";
      } else {
        changes = changes
          .map(change => {
            switch (change) {
              case "advertisedPrice":
                return "Price (Advertised)";
              case "salePrice":
                return "Price (Sale)";
              case "attachments":
              case "reports":
                return "Reports Added";
              case "photos":
                return "Photos Added";
              case "publishStatus":
                return listing?.publishStatus === "published"
                  ? "Published (Live)"
                  : "Published (Draft)";
              default:
                return formatTitleCase(change);
            }
          })
          .join(", ");
        formatted = (
          <Fragment>
            The update{" "}
            {showUpdatedDate &&
              !!listing.updated_at &&
              ` on ${getShortDate(listing.updated_at)} `}
            included: <strong>{changes}</strong>
          </Fragment>
        );
      }
    } else {
      formatted = (
        <Fragment>
          There have been <strong>no updates</strong> since you started watching
          this machine.
        </Fragment>
      );
    }

    this.setState({ changes: formatted });
  }

  /**
   * Handle compare listing checkbox
   * @public
   */
  handleCompare() {
    const { addToComparedListings, listing, removeFromComparedListings } =
      this.props;
    const { isCompared, showStatus } = this.state;

    if (isCompared) {
      removeFromComparedListings(listing.equipmentId);

      if (this.props.trackEvent) {
        this.props.trackEvent({
          category: "Compare",
          action: "Remove",
          label: this.formatTrackingEventLabel(listing),
        });
      }
    } else {
      addToComparedListings({
        ...listing,
        showStatus: showStatus,
      });

      if (this.props.trackEvent) {
        this.props.trackEvent({
          category: "Compare",
          action: "Add",
          label: this.formatTrackingEventLabel(listing),
        });
      }
    }
  }

  /**
   * Handle interactions with watch modal
   * @public
   */
  handleWatch() {
    this.setState(
      ({ openWatch }) => ({ openWatch: !openWatch }),
      () => {
        if (!this.state.openWatch && this.watchButton.current) {
          this.watchButton.current.focus();
        }
      }
    );
  }

  /**
   * Set is compared state
   * @public
   */
  setIsCompared() {
    const { comparedListings, listing } = this.props;
    const isCompared = !comparedListings.length
      ? false
      : comparedListings.some(
          compared => compared.equipmentId === listing.equipmentId
        );

    this.setState({ isCompared: isCompared });
  }

  /**
   * Set is watched state
   * @public
   */
  setIsWatched() {
    const { listing, userWatchedListings } = this.props;
    const listingId = !!listing && parseInt(listing.id, 10);
    const isWatched =
      !userWatchedListings.length ||
      (!listingId && listingId !== 0) ||
      isNaN(listingId)
        ? false
        : userWatchedListings.some(id => id === listingId);
    this.setState({ isWatched: isWatched });
  }

  componentDidMount() {
    this.getChanges();
    this.setIsCompared();
    this.setIsWatched();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.showChanges !== this.props.showChanges ||
      !isEqual(prevProps.listing, this.props.listing)
    ) {
      this.getChanges();
    }

    if (
      prevProps.comparedListings.length !==
        this.props.comparedListings.length ||
      !isEqual(prevProps.comparedListings, this.props.comparedListings)
    ) {
      this.setIsCompared();
    }

    if (
      prevProps.userWatchedListings.length !==
        this.props.userWatchedListings.length ||
      !isEqual(prevProps.userWatchedListings, this.props.userWatchedListings)
    ) {
      this.setIsWatched();
    }
  }

  render() {
    const { handleCompare, handleWatch, watchButton } = this;
    const { analyticsLabel, listing, targetDevice } = this.props;
    const { changes, isCompared, isWatched, openWatch, showStatus } =
      this.state;

    const linkProps =
      listing.status !== "remove"
        ? {
            analyticsAction: "View Details",
            analyticsLabel: analyticsLabel,
            sourceType: listing.sourceType,
            as: SourceTypeLink,
            to: `/${listing.slug}`,
          }
        : {};

    return targetDevice === "mobile" ? (
      <ListingStacked
        changes={changes}
        handleCompare={handleCompare}
        handleWatch={handleWatch}
        isCompared={isCompared}
        isWatched={isWatched}
        linkProps={linkProps}
        openWatch={openWatch}
        ref={watchButton}
        showStatus={showStatus}
        {...this.props}
      />
    ) : (
      <ResultsListingCard
        changes={changes}
        handleCompare={handleCompare}
        handleWatch={handleWatch}
        isCompared={isCompared}
        isWatched={isWatched}
        linkProps={linkProps}
        openWatch={openWatch}
        ref={watchButton}
        showStatus={showStatus}
        {...this.props}
      />
    );
  }
}

ResultsListing.propTypes = {
  addToComparedListings: func.isRequired,
  analyticsLabel: string,
  authenticated: bool,
  className: string,
  comparedListings: array,
  listing: object,
  isWatchPatch: bool,
  removeFromComparedListings: func.isRequired,
  showChanges: bool,
  showUpdatedDate: bool,
  targetDevice: string,
  trackEvent: func,
  trackModalView: func,
  userWatchedListings: array,
};

ResultsListing.defaultProps = {
  analyticsLabel: "results",
  authenticated: false,
  showUpdatedDate: true,
  userWatchedListings: [],
};

export default ResultsListing;
